import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { zhHK } from './locales/zh-HK';
import { en } from './locales/en';
import { zhCN } from './locales/zh-CN';
import { reapplyBrand } from './brand';

/**
 * HKSCA — i18n 初始化（三語：zh-HK 預設／en／zh-CN）
 *
 * ⭐ 兩層守衛（**缺 key 一定 build 紅**，唔准靜靜回 undefined）：
 *   ① 型別層：下面兩行 `const _parityX: typeof zhHK = …`
 *      → 缺 key／型別唔同 ＝ `npm run typecheck` 即刻報錯（build 已包含 typecheck）
 *   ② 機器層：`npm run check:locales`（用 vendored `@coeldery/parity-guard`）
 *
 * ⚠️ 一律以 **zh-HK 為基準**（唔可以反向寫 `typeof en = zhHK` ✗）
 */
const _parityEn: typeof zhHK = en;
const _parityZhCn: typeof zhHK = zhCN;
void _parityEn;
void _parityZhCn;

export const SUPPORTED_LOCALES = ['zh-HK', 'en', 'zh-CN'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = 'zh-HK';

export const resources = {
  'zh-HK': { translation: zhHK },
  en: { translation: en },
  'zh-CN': { translation: zhCN },
} as const;

const STORAGE_KEY = 'hksca.locale';

function pickInitialLocale(): SupportedLocale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && (SUPPORTED_LOCALES as readonly string[]).includes(saved)) {
      return saved as SupportedLocale;
    }
  } catch {
    /* 私隱模式可能停用 localStorage → 照用預設 */
  }
  const nav = typeof navigator !== 'undefined' ? navigator.language : DEFAULT_LOCALE;
  if (nav.toLowerCase().startsWith('zh-cn') || nav.toLowerCase().startsWith('zh-hans')) return 'zh-CN';
  if (nav.toLowerCase().startsWith('en')) return 'en';
  return DEFAULT_LOCALE;
}

void i18next.use(initReactI18next).init({
  resources,
  lng: pickInitialLocale(),
  fallbackLng: DEFAULT_LOCALE,
  interpolation: { escapeValue: false },
  /**
   * ⚠️ `keySeparator: false` —— 本 app 用**扁平**字典，而且部分 key 含點
   *    （例 `tab.aria`、`tab.benefits`，同 `@coeldery/bottom-nav` 等模組約定一致）。
   *    i18next 預設用 `.` 做嵌套分隔 → 唔關就會搵唔到。關咗之後 key 一律當字面量。
   */
  keySeparator: false,
});

i18next.on('languageChanged', (lng) => {
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    /* 忽略：只係記住偏好，唔影響功能 */
  }
});

/** 切換語言（只准傳支援嘅語言；UI 唔准自己寫語言字串） */
export function changeLocale(locale: SupportedLocale): void {
  void i18next.changeLanguage(locale);
  /** 品牌跟語言（`decisions/010` Phase A）—— title／tagline 由 brand.json 出 */
  reapplyBrand(locale);
}

export default i18next;
